/**
 * invalidateAfterLeave — the one cache refresh for "this action left the
 * detail of a record it may have removed" (#2802).
 *
 * A plain namespace invalidation refetches every active query under it,
 * including the detail that is still mounted while the surface leaves (the
 * drawer closes, the page navigates on the next commit). For a deleted or
 * cancelled record that refetch answers 404 and flashes the error.
 *
 * So the detail query is only marked stale (`refetchType: 'none'`), and the
 * rest of the namespace (lists, counts) is refreshed as before. The stale
 * mark is what makes a later reopen of the same record read it again. A
 * re-render that keeps the same query and the same `enabled` does not fetch
 * a stale query, so the still-mounted detail does not read it before it goes.
 */
import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';

/** The two key builders the refresh needs (`resourceKeys(slug)` fits). */
export interface LeaveKeys<TId> {
  all: () => QueryKey;
  detail: (id: TId) => QueryKey;
}

/** Mark the left record's detail stale without reading it, and refresh the
 *  rest of its namespace. Call it after a successful action that leaves
 *  the detail; the order relative to the leave does not matter. */
export function invalidateAfterLeave<TId>(
  queryClient: QueryClient,
  keys: LeaveKeys<TId>,
  id: TId,
): Promise<void> {
  const detailKey = keys.detail(id);
  const detailHash = hashKey(detailKey);
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: detailKey, exact: true, refetchType: 'none' }),
    queryClient.invalidateQueries({
      queryKey: keys.all(),
      // Compare keys, not `query.queryHash`: a client with its own
      // `queryKeyHashFn` stores a different hash, and the detail would refetch.
      predicate: (query) => hashKey(query.queryKey) !== detailHash,
    }),
  ]).then(() => undefined);
}
